/**
 * PriceVerificationPanel — admin section "Verificación Precios".
 *
 * Endpoints consumidos:
 *  - GET  /api/price-verification/sites
 *  - POST /api/price-verification/run             { n, sites }
 *  - GET  /api/price-verification/runs?limit=20
 *  - GET  /api/price-verification/runs/:id
 *  - GET  /api/price-verification/runs/:id/results?onlyBad=1
 *  - POST /api/price-verification/runs/:id/cancel
 *
 * UX:
 *  - Form arriba con N (default 200) + checkboxes de sitios + botón "Verificar precios".
 *  - Tabla de runs históricos con distribución y status badge.
 *  - Click sobre row abre drawer con resultados (filtro onlyBad).
 *  - Auto-refresh cada 10s si hay run status=running.
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE } from '../config';
import './PriceVerificationPanel.css';

const REFRESH_INTERVAL_MS = 10_000;

function fmtDateTime(value) {
  if (!value) return '—';
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    return d.toLocaleString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit', hour12: false,
    });
  } catch (_) { return '—'; }
}

function calcDuration(startedAt, finishedAt) {
  if (!startedAt) return '—';
  if (!finishedAt) return 'en curso';
  try {
    const start = new Date(startedAt).getTime();
    const end = new Date(finishedAt).getTime();
    if (Number.isNaN(start) || Number.isNaN(end)) return '—';
    const ms = end - start;
    if (ms < 1000) return `${ms} ms`;
    const s = Math.floor(ms / 1000);
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const rs = s % 60;
    if (m < 60) return `${m}m ${rs}s`;
    const h = Math.floor(m / 60);
    const rm = m % 60;
    return `${h}h ${rm}m`;
  } catch (_) { return '—'; }
}

function StatusBadge({ status }) {
  const norm = (status || '').toLowerCase();
  const cls = norm === 'completed' ? 'pv-status-success'
    : norm === 'running' ? 'pv-status-running'
    : norm === 'error' ? 'pv-status-error'
    : norm === 'cancelled' ? 'pv-status-partial'
    : 'pv-status-other';
  return <span className={`pv-status ${cls}`}>{status || '—'}</span>;
}

function ResultStatusPill({ status }) {
  const map = {
    EXACT: 'pv-pill-exact',
    OK_TRIVIAL: 'pv-pill-ok',
    OK_SMALL: 'pv-pill-ok',
    WARN_MEDIUM: 'pv-pill-warn',
    BAD_LARGE: 'pv-pill-bad',
    SCRAPE_ERR: 'pv-pill-err',
    NO_SCRAPE: 'pv-pill-err',
  };
  return <span className={`pv-pill ${map[status] || ''}`}>{status}</span>;
}

function DistributionBar({ run }) {
  const total = run.total_items || 0;
  if (total === 0) return <span className="pv-sub">—</span>;
  const e = run.exact_count || 0;
  const o = run.ok_count || 0;
  const w = run.warn_count || 0;
  const b = run.bad_count || 0;
  const er = run.error_count || 0;
  return (
    <div className="pv-dist">
      <div className="pv-dist-bar">
        {e > 0 && <span className="pv-seg pv-seg-exact" style={{ width: `${(e/total)*100}%` }} title={`EXACT: ${e}`} />}
        {o > 0 && <span className="pv-seg pv-seg-ok" style={{ width: `${(o/total)*100}%` }} title={`OK: ${o}`} />}
        {w > 0 && <span className="pv-seg pv-seg-warn" style={{ width: `${(w/total)*100}%` }} title={`WARN: ${w}`} />}
        {b > 0 && <span className="pv-seg pv-seg-bad" style={{ width: `${(b/total)*100}%` }} title={`BAD: ${b}`} />}
        {er > 0 && <span className="pv-seg pv-seg-err" style={{ width: `${(er/total)*100}%` }} title={`ERR: ${er}`} />}
      </div>
      <div className="pv-dist-counts">
        <span className="pv-c-exact">{e}</span>/
        <span className="pv-c-ok">{o}</span>/
        <span className="pv-c-warn">{w}</span>/
        <span className="pv-c-bad">{b}</span>/
        <span className="pv-c-err">{er}</span>
      </div>
    </div>
  );
}

function ResultsDrawer({ run, onClose }) {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [onlyBad, setOnlyBad] = useState(false);
  const [error, setError] = useState(null);

  const fetchResults = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (onlyBad) params.set('onlyBad', '1');
      const res = await fetch(`${API_BASE}/api/price-verification/runs/${run.id}/results?${params.toString()}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setResults(Array.isArray(j.results) ? j.results : []);
    } catch (e) {
      setError(e.message || 'Error');
    } finally {
      setLoading(false);
    }
  }, [run.id, onlyBad]);

  useEffect(() => { fetchResults(); }, [fetchResults]);

  return (
    <div className="pv-drawer-overlay" onClick={onClose}>
      <div className="pv-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="pv-drawer-header">
          <div>
            <h3>Run #{run.id} — resultados</h3>
            <div className="pv-sub">
              {fmtDateTime(run.started_at)} · {run.total_items} items · <StatusBadge status={run.status} />
            </div>
          </div>
          <button className="um-btn um-btn-ghost" onClick={onClose}>Cerrar</button>
        </div>
        <div className="pv-drawer-toolbar">
          <label>
            <input type="checkbox" checked={onlyBad} onChange={e => setOnlyBad(e.target.checked)} />
            {' '}Solo problemáticos (WARN/BAD/ERR)
          </label>
          <button className="um-btn um-btn-ghost" onClick={fetchResults} disabled={loading}>
            {loading ? 'Cargando…' : 'Refrescar'}
          </button>
          <span className="pv-sub">{results.length} resultados</span>
        </div>
        {error && <div className="um-error" style={{ margin: 14 }}>{error}</div>}
        <div className="pv-drawer-body">
          {loading && results.length === 0 ? (
            <div className="pv-empty">Cargando…</div>
          ) : results.length === 0 ? (
            <div className="pv-empty">Sin resultados.</div>
          ) : (
            <table className="um-table">
              <thead>
                <tr>
                  <th>Status</th>
                  <th>Sitio</th>
                  <th>COD</th>
                  <th className="pv-num">Esperado</th>
                  <th className="pv-num">Live</th>
                  <th className="pv-num">Diff %</th>
                  <th>URL</th>
                  <th>Error</th>
                </tr>
              </thead>
              <tbody>
                {results.map(r => (
                  <tr key={r.id}>
                    <td><ResultStatusPill status={r.status} /></td>
                    <td>{r.sitio || '—'}</td>
                    <td className="pv-mono">{r.cod_producto || '—'}</td>
                    <td className="pv-num">{r.exp_price != null ? Number(r.exp_price).toFixed(2) : '—'}</td>
                    <td className="pv-num">{r.live_price != null ? Number(r.live_price).toFixed(2) : '—'}</td>
                    <td className="pv-num">{r.diff_pct != null ? Number(r.diff_pct).toFixed(2) : '—'}</td>
                    <td className="pv-url">
                      {r.url ? <a href={r.url} target="_blank" rel="noreferrer">link</a> : '—'}
                    </td>
                    <td className="pv-sub">{r.error_msg || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

export default function PriceVerificationPanel() {
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState({});
  const [n, setN] = useState(200);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [launching, setLaunching] = useState(false);
  const [error, setError] = useState(null);
  const [openRun, setOpenRun] = useState(null);
  const [toast, setToast] = useState(null);
  const refreshTimerRef = useRef(null);

  // Toast auto-clear
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const fetchSites = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE}/api/price-verification/sites`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      const list = Array.isArray(j.sites) ? j.sites : [];
      setSites(list);
      // Default: todos seleccionados
      setSelectedSites(prev => {
        if (Object.keys(prev).length > 0) return prev;
        const init = {};
        list.forEach(s => { init[s.id] = true; });
        return init;
      });
    } catch (e) {
      setError(`No se pudieron cargar sitios: ${e.message}`);
    }
  }, []);

  const fetchRuns = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/price-verification/runs?limit=20`);
      if (res.status === 403) throw new Error('Sin permisos (admin+).');
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const j = await res.json();
      setRuns(Array.isArray(j.runs) ? j.runs : []);
      setError(null);
    } catch (e) {
      setError(e.message || 'Error');
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { fetchSites(); fetchRuns(false); }, [fetchSites, fetchRuns]);

  // Auto-refresh si hay algún run en status=running
  useEffect(() => {
    const hasRunning = runs.some(r => r.status === 'running');
    if (refreshTimerRef.current) {
      clearInterval(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
    if (hasRunning) {
      refreshTimerRef.current = setInterval(() => fetchRuns(true), REFRESH_INTERVAL_MS);
    }
    return () => {
      if (refreshTimerRef.current) clearInterval(refreshTimerRef.current);
    };
  }, [runs, fetchRuns]);

  const handleLaunch = async () => {
    const chosen = sites.filter(s => selectedSites[s.id]).map(s => s.id);
    if (chosen.length === 0) {
      setToast({ message: 'Elegí al menos un sitio.', type: 'error' });
      return;
    }
    const nn = Math.max(10, Math.min(1000, parseInt(n, 10) || 200));
    setLaunching(true);
    try {
      const res = await fetch(`${API_BASE}/api/price-verification/run`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ n: nn, sites: chosen }),
      });
      if (!res.ok) {
        const j = await res.json().catch(() => null);
        throw new Error(j?.error || `HTTP ${res.status}`);
      }
      const j = await res.json();
      setToast({ message: `Run #${j.runId} arrancado (n=${nn})`, type: 'success' });
      await fetchRuns(true);
    } catch (e) {
      setToast({ message: e.message || 'Error', type: 'error' });
    } finally {
      setLaunching(false);
    }
  };

  const handleCancel = async (runId) => {
    if (!window.confirm(`Cancelar run #${runId}?`)) return;
    try {
      const res = await fetch(`${API_BASE}/api/price-verification/runs/${runId}/cancel`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setToast({ message: `Run #${runId} cancelado.`, type: 'success' });
      fetchRuns(true);
    } catch (e) {
      setToast({ message: e.message || 'Error', type: 'error' });
    }
  };

  return (
    <>
      {/* FORM */}
      <div className="um-card" style={{ marginBottom: 16 }}>
        <div className="pv-form">
          <div className="pv-form-row">
            <label className="pv-form-label">Cantidad de productos a verificar</label>
            <input
              type="number"
              min="10"
              max="1000"
              value={n}
              onChange={e => setN(e.target.value)}
              className="pv-form-input"
            />
            <span className="pv-sub">(10–1000, default 200)</span>
          </div>
          <div className="pv-form-row">
            <label className="pv-form-label">Sitios a chequear</label>
            <div className="pv-sites">
              {sites.length === 0 && <span className="pv-sub">Cargando…</span>}
              {sites.map(s => (
                <label key={s.id} className="pv-site-check">
                  <input
                    type="checkbox"
                    checked={!!selectedSites[s.id]}
                    onChange={e => setSelectedSites(p => ({ ...p, [s.id]: e.target.checked }))}
                  />
                  {' '}{s.label}
                  {s.note && <span className="pv-sub"> · {s.note}</span>}
                </label>
              ))}
            </div>
          </div>
          <div className="pv-form-row">
            <button
              className="um-btn um-btn-primary"
              onClick={handleLaunch}
              disabled={launching || sites.length === 0}
            >
              {launching ? 'Lanzando…' : 'Verificar precios'}
            </button>
            <span className="pv-sub">
              El run corre async — el progreso aparece en la tabla de abajo.
            </span>
          </div>
        </div>
      </div>

      {/* RUNS TABLE */}
      <div className="um-card">
        <div className="um-toolbar">
          <div className="um-toolbar-left">
            <span className="um-count">{loading ? '…' : `${runs.length} runs`}</span>
            <span className="pv-sub">Auto-refresh 10s si hay un run activo</span>
          </div>
          <div>
            <button className="um-btn um-btn-ghost" onClick={() => fetchRuns(false)} disabled={loading}>
              Refrescar
            </button>
          </div>
        </div>
        {error && <div className="um-error" style={{ margin: 14 }}>{error}</div>}
        <div className="um-table-wrap">
          {loading && runs.length === 0 ? (
            <div className="pv-empty">Cargando…</div>
          ) : runs.length === 0 ? (
            <div className="pv-empty">No hay runs. Lanzá uno con el formulario de arriba.</div>
          ) : (
            <table className="um-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Inicio</th>
                  <th>N</th>
                  <th>Sitios</th>
                  <th>Status</th>
                  <th>Distribución (E/OK/W/B/ER)</th>
                  <th>Duración</th>
                  <th>Lanzado por</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {runs.map(r => (
                  <tr key={r.id} className="pv-run-row" onClick={() => setOpenRun(r)}>
                    <td className="pv-mono">#{r.id}</td>
                    <td className="pv-num">{fmtDateTime(r.started_at)}</td>
                    <td className="pv-num">{r.config?.n ?? r.total_items}</td>
                    <td className="pv-sub">{(r.config?.sites || []).join(', ') || '—'}</td>
                    <td><StatusBadge status={r.status} /></td>
                    <td><DistributionBar run={r} /></td>
                    <td className="pv-num">{calcDuration(r.started_at, r.finished_at)}</td>
                    <td className="pv-sub">{r.ran_by_email || '—'}</td>
                    <td onClick={e => e.stopPropagation()}>
                      {r.status === 'running' && (
                        <button className="um-btn um-btn-ghost pv-btn-sm" onClick={() => handleCancel(r.id)}>
                          Cancelar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {openRun && <ResultsDrawer run={openRun} onClose={() => setOpenRun(null)} />}

      {toast && (
        <div className={`um-toast ${toast.type === 'error' ? 'um-toast-error' : ''}`}>
          {toast.message}
        </div>
      )}
    </>
  );
}
